/**
 * Map canvas — M5.
 *
 * Render pipeline (every state change → one rAF):
 *   1. Background map image
 *   2. 5-ft grid lines
 *   3. Overlay (walls + doors) — subtle in token mode, vivid in overlay mode
 *   4. Tokens (skipping the one currently being dragged)
 *   5. Drag ghost at the cursor's grid cell (if dragging)
 *   6. Fog-of-war via LOS — for each PC, only cells with clear line-of-sight
 *      within visibility radius are revealed
 *
 * Mouse routing depends on `state.editMode`:
 *   - `tokens`: left-drag on token → move; right-click on token → menu;
 *     right-drag → pan; wheel → zoom.
 *   - `overlay`: left-drag → paint walls (mode locked at start of stroke);
 *     right-click on overlay cell → menu (add door / open / close / remove);
 *     right-drag → pan; wheel → zoom. Token interactions disabled.
 *
 * HTML5 drag-drop from the legend lives in dragDrop.ts (works in both modes
 * but dragDrop.ts pre-checks passability so legend drops onto walls fail
 * client-side too).
 */

import * as api from './api.js';
import { cellPx, gridCellCenter, screenToGrid, screenToLogical } from './grid.js';
import { tokenAtScreen } from './hitTest.js';
import { visibleCells } from './los.js';
import { isCellOccupied } from './occupancy.js';
import { applyPaint, cellKey, getCell, isPassable, paintModeFromCell } from './overlay.js';
import { getState, markExploredCells, saveViewportDebounced, setOverlay, setState, upsertToken } from './state.js';
import type { AppState, ExploredCells, MapOverlay, OverlayCell, Token, Viewport } from './types.js';

const DEFAULT_NPC_IMAGE = '/default-npc.svg';
const MIN_ZOOM = 0.2;
const MAX_ZOOM = 5;
const ZOOM_STEP = 0.1;

/** Image cache by URL. */
const imageCache = new Map<string, HTMLImageElement>();

/** Reused offscreen fog buffer; resized to match the current map image. */
let fogBuffer: HTMLCanvasElement | null = null;

interface CanvasController {
  render: (state: AppState) => void;
  destroy: () => void;
  hitTest: (clientX: number, clientY: number) => Token | null;
  getCellPx: () => number | null;
  /** Recenter the viewport on a grid cell, preserving zoom. */
  centerOnCell: (gridX: number, gridY: number) => void;
}

export interface TokenContextEventDetail {
  token: Token;
  clientX: number;
  clientY: number;
}

export interface OverlayContextEventDetail {
  /** Grid coords of the right-clicked cell. */
  gridX: number;
  gridY: number;
  /** Current cell value (null for free cells). */
  cell: OverlayCell | null;
  clientX: number;
  clientY: number;
}

export function mountCanvas(canvas: HTMLCanvasElement): CanvasController {
  const ctx = canvas.getContext('2d');
  if (ctx === null) {
    throw new Error('2D canvas context unavailable');
  }

  resizeToContainer(canvas);
  const onResize = (): void => {
    resizeToContainer(canvas);
    drawScene(canvas, ctx, getState(), liveDrag);
  };
  window.addEventListener('resize', onResize);

  // --- transient interaction state ------------------------------------------

  /** Middle-button drag for panning. Right-click is reserved for context menus. */
  type PanState = { startX: number; startY: number; vx: number; vy: number };
  let panState: PanState | null = null;

  type LiveDrag = { tokenId: number; clientX: number; clientY: number };
  let liveDrag: LiveDrag | null = null;

  type PaintStroke = {
    mode: 'paint-solid' | 'paint-terrain' | 'erase';
    overlay: MapOverlay; // working copy; flushed via setOverlay on each cell change
    lastCell: { x: number; y: number };
  };
  let paintStroke: PaintStroke | null = null;

  // --- mouse handlers --------------------------------------------------------

  const onMouseDown = (e: MouseEvent): void => {
    const state = getState();

    if (e.button === 0) {
      // Left click. Routing depends on edit mode.
      const cell = computeCellPx();
      if (cell === null) return;
      const rect = canvas.getBoundingClientRect();

      if (state.editMode === 'overlay') {
        const { grid_x, grid_y } = screenToGrid(e.clientX - rect.left, e.clientY - rect.top, cell, state.viewport);
        const startOverlay = state.currentMap?.overlay ?? null;
        const rawMode = paintModeFromCell(startOverlay, grid_x, grid_y);
        const mode = rawMode === 'paint'
          ? (state.overlaySubMode === 'terrain' ? 'paint-terrain' : 'paint-solid')
          : rawMode;
        const next = applyPaint(startOverlay, grid_x, grid_y, mode);
        setOverlay(next);
        paintStroke = { mode, overlay: next, lastCell: { x: grid_x, y: grid_y } };
        e.preventDefault();
        return;
      }

      // tokens mode: existing token-drag flow (only placed tokens are hit-testable)
      const t = tokenAtScreen(
        e.clientX - rect.left, e.clientY - rect.top,
        sortForRender(state.tokens.filter((tk) => tk.is_placed)), cell, state.viewport,
      );
      if (t !== null) {
        liveDrag = { tokenId: t.id, clientX: e.clientX, clientY: e.clientY };
        setState({ draggingTokenId: t.id });
        e.preventDefault();
      }
      return;
    }

    if (e.button === 1) {
      // Middle button → start panning. Suppress browser auto-scroll.
      e.preventDefault();
      const v = state.viewport;
      panState = { startX: e.clientX, startY: e.clientY, vx: v.x, vy: v.y };
    }
    // Right button (e.button === 2) is handled by the contextmenu event.
  };

  const onMouseMove = (e: MouseEvent): void => {
    const state = getState();

    // Active paint stroke in edit mode.
    if (paintStroke !== null) {
      const cellPxValue = computeCellPx();
      if (cellPxValue === null) return;
      const rect = canvas.getBoundingClientRect();
      const { grid_x, grid_y } = screenToGrid(e.clientX - rect.left, e.clientY - rect.top, cellPxValue, state.viewport);
      if (grid_x === paintStroke.lastCell.x && grid_y === paintStroke.lastCell.y) return;
      paintStroke.overlay = applyPaint(paintStroke.overlay, grid_x, grid_y, paintStroke.mode);
      paintStroke.lastCell = { x: grid_x, y: grid_y };
      setOverlay(paintStroke.overlay);
      return;
    }

    if (liveDrag !== null) {
      liveDrag.clientX = e.clientX;
      liveDrag.clientY = e.clientY;
      drawScene(canvas, ctx, state, liveDrag);
      return;
    }
    if (panState !== null) {
      const next: Viewport = {
        x: panState.vx + (e.clientX - panState.startX),
        y: panState.vy + (e.clientY - panState.startY),
        zoom: state.viewport.zoom,
      };
      setState({ viewport: next });
      saveViewportDebounced(next);
    }
  };

  const onMouseUp = (e: MouseEvent): void => {
    if (e.button === 0) {
      if (paintStroke !== null) {
        // Final overlay state already set during the stroke; debounced save
        // in state.ts will flush within 500ms.
        paintStroke = null;
        return;
      }
      if (liveDrag !== null) {
        const cellPxValue = computeCellPx();
        if (cellPxValue !== null) {
          const rect = canvas.getBoundingClientRect();
          const { grid_x, grid_y } = screenToGrid(
            e.clientX - rect.left, e.clientY - rect.top,
            cellPxValue, getState().viewport,
          );
          const id = liveDrag.tokenId;
          // Pre-check passability + occupancy so we don't even attempt the API call.
          const dropState = getState();
          const overlay = dropState.currentMap?.overlay ?? null;
          if (!isPassable(overlay, grid_x, grid_y)) {
            liveDrag = null;
            setState({ draggingTokenId: null });
            console.warn('Cannot place token on a wall or closed door');
            return;
          }
          if (isCellOccupied(dropState.tokens, grid_x, grid_y, id)) {
            liveDrag = null;
            setState({ draggingTokenId: null });
            console.warn('Cannot place token on an occupied cell');
            return;
          }
          liveDrag = null;
          setState({ draggingTokenId: null });
          void api.updateToken(id, { grid_x, grid_y })
            .then((updated) => upsertToken(updated))
            .catch((err) => {
              console.error('Failed to move token', err);
            });
        } else {
          liveDrag = null;
          setState({ draggingTokenId: null });
        }
      }
      return;
    }
    if (e.button === 1 && panState !== null) {
      panState = null;
    }
    // Right button: nothing to do — contextmenu event handles dispatch.
  };

  const onContextMenu = (e: MouseEvent): void => {
    // Suppress the browser menu and dispatch our own based on what's hit.
    e.preventDefault();
    const state = getState();
    const cell = computeCellPx();
    if (cell === null) return;
    const rect = canvas.getBoundingClientRect();
    const v = state.viewport;

    // Token mode: tokens win, but doors are also actionable. Walls aren't.
    // Overlay mode: any overlay cell is actionable.
    if (state.editMode === 'tokens') {
      const t = tokenAtScreen(
        e.clientX - rect.left, e.clientY - rect.top,
        sortForRender(state.tokens.filter((tk) => tk.is_placed)), cell, v,
      );
      if (t !== null) {
        const detail: TokenContextEventDetail = { token: t, clientX: e.clientX, clientY: e.clientY };
        canvas.dispatchEvent(new CustomEvent('mapnmove:token-context', { detail }));
        return;
      }
      const { grid_x, grid_y } = screenToGrid(e.clientX - rect.left, e.clientY - rect.top, cell, v);
      const cellAt = getCell(state.currentMap?.overlay ?? null, grid_x, grid_y);
      if (cellAt !== null && cellAt.kind === 'door') {
        const detail: OverlayContextEventDetail = {
          gridX: grid_x, gridY: grid_y, cell: cellAt,
          clientX: e.clientX, clientY: e.clientY,
        };
        canvas.dispatchEvent(new CustomEvent('mapnmove:overlay-context', { detail }));
      }
    } else {
      const { grid_x, grid_y } = screenToGrid(e.clientX - rect.left, e.clientY - rect.top, cell, v);
      const cellAt = getCell(state.currentMap?.overlay ?? null, grid_x, grid_y);
      if (cellAt !== null) {
        const detail: OverlayContextEventDetail = {
          gridX: grid_x, gridY: grid_y, cell: cellAt,
          clientX: e.clientX, clientY: e.clientY,
        };
        canvas.dispatchEvent(new CustomEvent('mapnmove:overlay-context', { detail }));
      }
    }
  };

  const onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    const direction = e.deltaY < 0 ? 1 : -1;
    zoomAt(canvas, direction * ZOOM_STEP, e.clientX, e.clientY);
  };

  const onKeyDown = (e: KeyboardEvent): void => {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
    const rect = canvas.getBoundingClientRect();
    if (e.key === '+' || e.key === '=') {
      zoomAt(canvas, ZOOM_STEP, rect.left + rect.width / 2, rect.top + rect.height / 2);
    } else if (e.key === '-' || e.key === '_') {
      zoomAt(canvas, -ZOOM_STEP, rect.left + rect.width / 2, rect.top + rect.height / 2);
    }
  };

  canvas.addEventListener('mousedown', onMouseDown);
  canvas.addEventListener('contextmenu', onContextMenu);
  canvas.addEventListener('wheel', onWheel, { passive: false });
  window.addEventListener('mousemove', onMouseMove);
  window.addEventListener('mouseup', onMouseUp);
  window.addEventListener('keydown', onKeyDown);

  function computeCellPx(): number | null {
    const map = getState().currentMap;
    if (map === null) return null;
    const image = imageCache.get(map.image_path);
    if (image === undefined || !image.complete || image.naturalWidth === 0) return null;
    return cellPx({ image_path: map.image_path, width_ft: map.width_ft }, image.naturalWidth);
  }

  const render = (state: AppState): void => {
    drawScene(canvas, ctx, state, liveDrag);
  };

  const destroy = (): void => {
    canvas.removeEventListener('mousedown', onMouseDown);
    canvas.removeEventListener('contextmenu', onContextMenu);
    canvas.removeEventListener('wheel', onWheel);
    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('mouseup', onMouseUp);
    window.removeEventListener('keydown', onKeyDown);
    window.removeEventListener('resize', onResize);
  };

  const hitTest = (clientX: number, clientY: number): Token | null => {
    const cell = computeCellPx();
    if (cell === null) return null;
    const rect = canvas.getBoundingClientRect();
    return tokenAtScreen(
      clientX - rect.left, clientY - rect.top,
      sortForRender(getState().tokens.filter((t) => t.is_placed)), cell, getState().viewport,
    );
  };

  const centerOnCell = (gridX: number, gridY: number): void => {
    const cell = computeCellPx();
    if (cell === null) return;
    const v = getState().viewport;
    const rect = canvas.getBoundingClientRect();
    const { cx, cy } = gridCellCenter(gridX, gridY, cell);
    // Inverse of the draw transform (screen = logical*zoom + viewport):
    // place the cell center at the canvas center, zoom unchanged.
    const next: Viewport = {
      x: rect.width / 2 - cx * v.zoom,
      y: rect.height / 2 - cy * v.zoom,
      zoom: v.zoom,
    };
    setState({ viewport: next });
    saveViewportDebounced(next);
  };

  return { render, destroy, hitTest, getCellPx: computeCellPx, centerOnCell };
}

function resizeToContainer(canvas: HTMLCanvasElement): void {
  const parent = canvas.parentElement;
  if (parent === null) return;
  const rect = parent.getBoundingClientRect();
  const dpr = window.devicePixelRatio || 1;
  canvas.width = Math.max(1, Math.floor(rect.width * dpr));
  canvas.height = Math.max(1, Math.floor(rect.height * dpr));
  canvas.style.width = `${rect.width}px`;
  canvas.style.height = `${rect.height}px`;
}

function zoomAt(canvas: HTMLCanvasElement, delta: number, screenX: number, screenY: number): void {
  const v = getState().viewport;
  const newZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, v.zoom + delta));
  if (newZoom === v.zoom) return;

  const rect = canvas.getBoundingClientRect();
  const localX = screenX - rect.left;
  const localY = screenY - rect.top;
  const before = screenToLogical(localX, localY, v);
  const next: Viewport = {
    zoom: newZoom,
    x: localX - before.x * newZoom,
    y: localY - before.y * newZoom,
  };
  setState({ viewport: next });
  saveViewportDebounced(next);
}

function drawScene(
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
  state: AppState,
  liveDrag: { tokenId: number; clientX: number; clientY: number } | null,
): void {
  const dpr = window.devicePixelRatio || 1;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.fillStyle = '#1a1a1a';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const map = state.currentMap;
  if (map === null) {
    drawPlaceholder(canvas, ctx, 'No map selected');
    return;
  }

  const image = loadImage(map.image_path, () => drawScene(canvas, ctx, getState(), liveDrag));
  if (image === null) {
    drawPlaceholder(canvas, ctx, 'Loading map…');
    return;
  }

  const v = state.viewport;
  ctx.setTransform(v.zoom * dpr, 0, 0, v.zoom * dpr, v.x * dpr, v.y * dpr);

  // 1. Map image.
  ctx.drawImage(image, 0, 0);

  const cell = cellPx({ image_path: map.image_path, width_ft: map.width_ft }, image.naturalWidth);

  // 2. Grid.
  drawGrid(ctx, image.naturalWidth, image.naturalHeight, cell);

  // 3. Overlay (walls/doors).
  drawOverlay(ctx, map.overlay, cell, state.editMode === 'overlay');

  // Compute the union of currently-visible cells across all placed PCs once,
  // then reuse for token gating, fog rendering, and explored-set update.
  // In edit-map mode we skip the computation entirely (fog disabled, all tokens shown).
  const editingMap = state.editMode === 'overlay';
  const currentlyVisible: Set<string> = editingMap
    ? new Set<string>()
    : computeCurrentlyVisibleCells(state.tokens, map.overlay);

  // Grow the persistent explored set with anything newly seen this frame.
  if (!editingMap && currentlyVisible.size > 0) {
    markExploredCells(currentlyVisible);
  }

  // 4. Tokens (skip dragged). During an encounter the active token gets a
  //    glow and already-acted tokens are dimmed. NPCs outside current LOS
  //    are hidden in normal play; in edit mode all tokens are shown.
  const encounter = map.encounter;
  let activeTokenId: number | null = null;
  const actedIds = new Set<number>();
  if (encounter != null) {
    activeTokenId = encounter.activeTokenId;
    const activeIdx = encounter.order.findIndex((e) => e.tokenId === encounter.activeTokenId);
    if (activeIdx > 0) {
      for (let i = 0; i < activeIdx; i++) actedIds.add(encounter.order[i]!.tokenId);
    }
  }
  // Stable-sort so downed tokens render first (below) and non-downed render
  // last (above) — when a standing token steps onto a fallen one, it stays
  // on top visually. Hit-testing reuses the same order so clicks on a stack
  // grab the topmost (non-downed) token first.
  const renderOrder = sortForRender(state.tokens);
  for (const token of renderOrder) {
    if (token.id === state.draggingTokenId) continue;
    if (!token.is_placed) continue; // lives in the legend / initiative panel only
    if (!editingMap && token.type === 'npc' && !currentlyVisible.has(cellKey(token.grid_x, token.grid_y))) {
      continue; // NPC outside LOS — hidden
    }
    const role: TokenRole =
      token.id === activeTokenId ? 'active' : actedIds.has(token.id) ? 'acted' : 'normal';
    drawToken(ctx, token, cell, role);
  }

  // 5. Drag ghost.
  if (liveDrag !== null) {
    const dragged = state.tokens.find((t) => t.id === liveDrag.tokenId);
    if (dragged !== undefined) {
      const rect = canvas.getBoundingClientRect();
      const { grid_x, grid_y } = screenToGrid(
        liveDrag.clientX - rect.left,
        liveDrag.clientY - rect.top,
        cell, v,
      );
      // Tint the ghost over impassable cells so the GM sees it'll fail:
      // red for walls/doors, orange for terrain, purple for occupied.
      // Priority: wall > terrain > occupied > none.
      let tint: 'none' | 'wall' | 'terrain' | 'occupied' = 'none';
      if (!isPassable(map.overlay, grid_x, grid_y)) {
        const overlayCell = getCell(map.overlay, grid_x, grid_y);
        tint = overlayCell !== null && overlayCell.kind === 'terrain' ? 'terrain' : 'wall';
      } else if (isCellOccupied(state.tokens, grid_x, grid_y, dragged.id)) {
        tint = 'occupied';
      }
      drawGhostToken(ctx, dragged, cell, grid_x, grid_y, tint);
    }
  }

  // 6. Three-state fog of war — suppressed in overlay-edit mode so the GM
  //    can see the entire map while painting walls/doors/terrain.
  if (!editingMap) {
    drawFogLos(ctx, image.naturalWidth, image.naturalHeight, cell, currentlyVisible, map.explored);
  }
}

/**
 * Stable order: downed tokens first (drawn below / hit-tested last), non-downed
 * last (drawn above / hit-tested first). Used by both the render loop and the
 * hit-test callsites so visual and interactive z-order stay in sync.
 */
function sortForRender(tokens: readonly Token[]): Token[] {
  return [...tokens].sort((a, b) =>
    a.is_downed === b.is_downed ? 0 : a.is_downed ? -1 : 1,
  );
}

/** Union of every cell currently in any placed PC's line of sight. */
function computeCurrentlyVisibleCells(tokens: readonly Token[], overlay: MapOverlay | null): Set<string> {
  const out = new Set<string>();
  for (const t of tokens) {
    if (t.type !== 'pc') continue;
    if (!t.is_placed) continue;
    const visibilityCells = Math.max(0, Math.floor(t.visibility_ft / 5));
    const seen = visibleCells(t.grid_x, t.grid_y, visibilityCells, overlay);
    for (const k of seen) out.add(k);
  }
  return out;
}

function drawPlaceholder(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D, text: string): void {
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.fillStyle = '#888';
  ctx.font = '16px system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, canvas.width / 2, canvas.height / 2);
}

function drawGrid(ctx: CanvasRenderingContext2D, w: number, h: number, cell: number): void {
  ctx.strokeStyle = 'rgba(255,255,255,0.25)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (let x = 0; x <= w; x += cell) {
    ctx.moveTo(x, 0);
    ctx.lineTo(x, h);
  }
  for (let y = 0; y <= h; y += cell) {
    ctx.moveTo(0, y);
    ctx.lineTo(w, y);
  }
  ctx.stroke();
}

function drawOverlay(
  ctx: CanvasRenderingContext2D,
  overlay: MapOverlay | null,
  cellPxValue: number,
  editMode: boolean,
): void {
  if (overlay === null) return;
  const wallFill = editMode ? 'rgba(239, 68, 68, 0.55)' : 'rgba(0, 0, 0, 0.45)';
  const wallStroke = editMode ? 'rgba(239, 68, 68, 0.95)' : 'rgba(0, 0, 0, 0.7)';
  const doorClosedFill = editMode ? 'rgba(180, 110, 50, 0.7)' : 'rgba(120, 75, 35, 0.55)';
  const doorOpenFill = editMode ? 'rgba(180, 110, 50, 0.35)' : 'rgba(120, 75, 35, 0.18)';
  const doorStroke = editMode ? 'rgba(220, 160, 80, 1)' : 'rgba(160, 110, 60, 0.9)';

  for (const [key, cell] of Object.entries(overlay)) {
    const [xs, ys] = key.split(',');
    const gx = Number(xs);
    const gy = Number(ys);
    if (!Number.isFinite(gx) || !Number.isFinite(gy)) continue;
    const x0 = gx * cellPxValue;
    const y0 = gy * cellPxValue;

    if (cell.kind === 'solid') {
      ctx.fillStyle = wallFill;
      ctx.fillRect(x0, y0, cellPxValue, cellPxValue);
      ctx.strokeStyle = wallStroke;
      ctx.lineWidth = Math.max(1, cellPxValue * 0.04);
      ctx.strokeRect(x0 + 0.5, y0 + 0.5, cellPxValue - 1, cellPxValue - 1);
    } else if (cell.kind === 'door') {
      ctx.fillStyle = cell.open ? doorOpenFill : doorClosedFill;
      ctx.fillRect(x0, y0, cellPxValue, cellPxValue);
      ctx.strokeStyle = doorStroke;
      ctx.lineWidth = Math.max(1, cellPxValue * 0.08);
      // Symbol: a vertical bar for closed, an arc for open.
      const cx = x0 + cellPxValue / 2;
      const cy = y0 + cellPxValue / 2;
      const r = cellPxValue * 0.35;
      ctx.beginPath();
      if (cell.open) {
        ctx.arc(cx, cy, r, Math.PI, 1.5 * Math.PI); // quarter arc
      } else {
        ctx.moveTo(cx - r * 0.8, cy);
        ctx.lineTo(cx + r * 0.8, cy);
      }
      ctx.stroke();
    } else if (cell.kind === 'terrain') {
      if (!editMode) continue;
      ctx.fillStyle = 'rgba(245, 158, 11, 0.40)';
      ctx.fillRect(x0, y0, cellPxValue, cellPxValue);
      ctx.strokeStyle = 'rgba(245, 158, 11, 0.85)';
      ctx.lineWidth = Math.max(1, cellPxValue * 0.04);
      ctx.strokeRect(x0 + 0.5, y0 + 0.5, cellPxValue - 1, cellPxValue - 1);
      // X symbol to distinguish from walls.
      const margin = cellPxValue * 0.25;
      ctx.lineWidth = Math.max(1, cellPxValue * 0.06);
      ctx.beginPath();
      ctx.moveTo(x0 + margin, y0 + margin);
      ctx.lineTo(x0 + cellPxValue - margin, y0 + cellPxValue - margin);
      ctx.moveTo(x0 + cellPxValue - margin, y0 + margin);
      ctx.lineTo(x0 + margin, y0 + cellPxValue - margin);
      ctx.stroke();
    }
  }
}

type TokenRole = 'normal' | 'active' | 'acted';

function drawToken(ctx: CanvasRenderingContext2D, token: Token, cell: number, role: TokenRole = 'normal'): void {
  const { cx, cy } = gridCellCenter(token.grid_x, token.grid_y, cell);
  const radius = cell * 0.45;

  // Active token: a gold glow ring behind it.
  if (role === 'active') {
    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, radius * 1.28, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(250, 204, 21, 0.35)';
    ctx.fill();
    ctx.lineWidth = Math.max(2, cell * 0.06);
    ctx.strokeStyle = '#facc15';
    ctx.beginPath();
    ctx.arc(cx, cy, radius * 1.28, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  // Tokens always render at full opacity on the map; "acted" dimming is
  // shown only in the initiative panel, not on the battlefield.
  drawTokenAt(ctx, token, cell, cx, cy, 1, 'none');

  if (token.status_text !== null && token.status_text !== '') {
    drawStatusLabel(ctx, cx, cy - cell * 0.45 - 6, token.status_text);
  }
}

function drawGhostToken(
  ctx: CanvasRenderingContext2D, token: Token, cell: number,
  gridX: number, gridY: number, tint: 'none' | 'wall' | 'terrain' | 'occupied',
): void {
  const { cx, cy } = gridCellCenter(gridX, gridY, cell);
  drawTokenAt(ctx, token, cell, cx, cy, 0.6, tint);
}

function drawTokenAt(
  ctx: CanvasRenderingContext2D,
  token: Token,
  cell: number,
  cx: number,
  cy: number,
  alpha: number,
  tint: 'none' | 'wall' | 'terrain' | 'occupied',
): void {
  const radius = cell * 0.45;
  const imgUrl = token.image_path !== null ? `/${token.image_path}` : DEFAULT_NPC_IMAGE;
  const image = loadImage(imgUrl, () => {
    drawScene(ctx.canvas, ctx, getState(), null);
  });

  ctx.save();
  ctx.globalAlpha = alpha;
  if (token.is_downed) {
    ctx.filter = 'grayscale(100%)';
  }
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.closePath();
  ctx.clip();
  if (image !== null) {
    ctx.drawImage(image, cx - radius, cy - radius, radius * 2, radius * 2);
  } else {
    ctx.fillStyle = '#444';
    ctx.fillRect(cx - radius, cy - radius, radius * 2, radius * 2);
  }
  ctx.restore();

  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.lineWidth = Math.max(2, cell * 0.05);
  const tintColor =
    tint === 'wall' ? '#ef4444' :
    tint === 'terrain' ? '#f59e0b' :
    tint === 'occupied' ? '#a855f7' :
    null;
  ctx.strokeStyle = tintColor ?? (token.type === 'pc' ? '#3b82f6' : '#ef4444');
  ctx.stroke();
  ctx.restore();
}

function drawStatusLabel(ctx: CanvasRenderingContext2D, x: number, y: number, text: string): void {
  ctx.font = '12px system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'bottom';
  const padding = 4;
  const metrics = ctx.measureText(text);
  const w = metrics.width + padding * 2;
  const h = 18;
  ctx.fillStyle = 'rgba(0,0,0,0.7)';
  roundRect(ctx, x - w / 2, y - h, w, h, 4);
  ctx.fill();
  ctx.fillStyle = '#fff';
  ctx.fillText(text, x, y - 3);
}

/**
 * Three-state fog of war.
 *
 *   - Currently visible (in `currentlyVisible`)         → fully cleared
 *   - Explored but not currently visible (in `explored`) → partially cleared → grey veil
 *   - Never explored (neither set contains the cell)     → opaque black
 *
 * Implementation: paint the fog buffer fully opaque black, then use
 * destination-out to carve holes. Alpha of the carve controls how much
 * fog remains (1.0 = clear, 0.55 = grey veil).
 */
function drawFogLos(
  ctx: CanvasRenderingContext2D,
  imageW: number,
  imageH: number,
  cellPxValue: number,
  currentlyVisible: Set<string>,
  explored: ExploredCells | null,
): void {
  if (fogBuffer === null) {
    fogBuffer = document.createElement('canvas');
  }
  if (fogBuffer.width !== imageW || fogBuffer.height !== imageH) {
    fogBuffer.width = imageW;
    fogBuffer.height = imageH;
  }
  const fctx = fogBuffer.getContext('2d');
  if (fctx === null) return;

  fctx.setTransform(1, 0, 0, 1, 0, 0);
  fctx.clearRect(0, 0, imageW, imageH);
  // Fully opaque black for "never explored". Currently-visible and explored
  // cells will carve through it below.
  fctx.fillStyle = 'rgba(0,0,0,1)';
  fctx.fillRect(0, 0, imageW, imageH);

  fctx.globalCompositeOperation = 'destination-out';

  // Pass 1: explored-but-not-currently-visible → partial clear (grey veil).
  if (explored !== null) {
    fctx.fillStyle = 'rgba(0,0,0,0.55)';
    for (const key of Object.keys(explored)) {
      if (currentlyVisible.has(key)) continue; // handled in pass 2
      paintFogCell(fctx, key, cellPxValue);
    }
  }

  // Pass 2: currently visible → fully clear.
  fctx.fillStyle = 'rgba(0,0,0,1)';
  for (const key of currentlyVisible) {
    paintFogCell(fctx, key, cellPxValue);
  }

  fctx.globalCompositeOperation = 'source-over';
  ctx.drawImage(fogBuffer, 0, 0);
}

/** Punch a single cell-sized rectangle into the fog buffer at the given key. */
function paintFogCell(fctx: CanvasRenderingContext2D, key: string, cellPxValue: number): void {
  const [xs, ys] = key.split(',');
  const gx = Number(xs);
  const gy = Number(ys);
  if (!Number.isFinite(gx) || !Number.isFinite(gy)) return;
  fctx.fillRect(
    gx * cellPxValue - 0.5,
    gy * cellPxValue - 0.5,
    cellPxValue + 1,
    cellPxValue + 1,
  );
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function loadImage(url: string, onLoad: () => void): HTMLImageElement | null {
  const cached = imageCache.get(url);
  if (cached !== undefined) {
    return cached.complete && cached.naturalWidth > 0 ? cached : null;
  }
  const img = new Image();
  img.addEventListener('load', onLoad, { once: true });
  img.src = url;
  imageCache.set(url, img);
  return null;
}
